// src/auth/tokenManager.ts
/**
 * TokenManager handles access and refresh tokens with a refresh lock to avoid
 * concurrent refresh requests. Tokens are kept in memory only (no localStorage).
 *
 * @example
 * const manager = new TokenManager();
 * manager.setTokens('access-abc', 'refresh-xyz');
 * const token = manager.getToken(); // 'access-abc'
 */
export class TokenManager {
    _accessToken = null;
    _refreshToken = null;
    _isRefreshing = false;
    _pendingRequests = [];
    /**
     * Returns the current access token, or null if none is set.
     * @returns {string | null} The current access token.
     */
    getToken() {
        return this._accessToken;
    }
    /**
     * Sets the access and refresh tokens in memory.
     * @param {string} access - The new access token.
     * @param {string} refresh - The new refresh token.
     */
    setTokens(access, refresh) {
        this._accessToken = access;
        this._refreshToken = refresh;
    }
    /**
     * Queue a request to be retried once an ongoing refresh completes.
     * @param {RetryCallback} callback - The retry function.
     * @param {RequestConfig} originalRequest - The original request configuration.
     * @returns {Promise<any>} Promise resolving to the retried request's response.
     */
    enqueueRetry(callback, originalRequest) {
        return new Promise((resolve, reject) => {
            this._pendingRequests.push(() => {
                callback(originalRequest).then(resolve).catch(reject);
            });
        });
    }
    /**
     * Performs the actual token refresh against the given endpoint.
     * Implements a refresh lock: if a refresh is already in progress, the call is
     * queued and retried once the first refresh resolves.
     *
     * @param {string} refreshEndpoint - The API endpoint to call for refreshing the token.
     * @param {RetryCallback} retryCallback - Function to retry the original request after refresh.
     * @param {RequestConfig} originalRequest - The original request that triggered the 401.
     * @returns {Promise<any>} Promise resolving to the response of the retried request.
     * @throws {Error} Throws 'Refresh failed' if the refresh endpoint returns a non-OK response.
     */
    async performRefresh(refreshEndpoint, retryCallback, originalRequest) {
        if (this._isRefreshing) {
            // Another refresh is in progress — queue and wait
            return this.enqueueRetry(retryCallback, originalRequest);
        }
        this._isRefreshing = true;
        try {
            const response = await fetch(refreshEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken: this._refreshToken }),
            });
            if (!response.ok) {
                this.logout();
                throw new Error('Refresh failed');
            }
            const data = await response.json();
            this.setTokens(data.accessToken, data.refreshToken);
            // Flush the queue: retry all waiting requests with the new token
            const queued = [...this._pendingRequests];
            this._pendingRequests = [];
            queued.forEach((fn) => fn());
            return retryCallback(originalRequest);
        }
        finally {
            this._isRefreshing = false;
        }
    }
    /**
     * Handles a 401 Unauthorized response by invoking the refresh flow.
     *
     * @param {string} refreshEndpoint - The endpoint to obtain a new access token.
     * @param {RetryCallback} retryCallback - Function to retry the original request after refresh.
     * @param {RequestConfig} originalRequest - The request that received the 401.
     * @returns {Promise<any>} Promise resolving to the retried request's response.
     */
    async handle401(refreshEndpoint, retryCallback, originalRequest) {
        return this.performRefresh(refreshEndpoint, retryCallback, originalRequest);
    }
    /**
     * Clears stored tokens. Called when refresh fails or the user logs out.
     */
    logout() {
        this._accessToken = null;
        this._refreshToken = null;
    }
}
//# sourceMappingURL=tokenManager.js.map